import { createI18n } from 'vue-i18n'
import it from './it.json'
import en from './en.json'
import enErrors from '@volverjs/zod-vue-i18n/locales/v4/en.json'
import itErrors from '@volverjs/zod-vue-i18n/locales/v4/it.json'

export const SUPPORTED_LOCALES = ['it', 'en'] as const
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

function detectLocale(): SupportedLocale {
    const browserLang = navigator.language.split('-')[0]
    return (SUPPORTED_LOCALES as readonly string[]).includes(browserLang)
        ? (browserLang as SupportedLocale)
        : 'en'
}

/*
 * Setup i18n
 * https://vue-i18n.intlify.dev/
 */

export const i18n = createI18n<false>({
    locale: detectLocale(),
    legacy: false,
    globalInjection: true,
    fallbackWarn: false,
    messages: {
        it: {
            ...it,
            errors: { ...itErrors, ...it.errors },
        },
        en: {
            ...en,
            errors: { ...enErrors, ...en.errors },
        },
    },
    numberFormats: {
        it: {
            currencyEUR: {
                style: 'currency',
                currency: 'EUR',
                currencySign: 'accounting',
                notation: 'standard',
                maximumFractionDigits: 4,
                useGrouping: true,
            },
            currencyUSD: {
                style: 'currency',
                currency: 'USD',
                currencySign: 'accounting',
                notation: 'standard',
                maximumFractionDigits: 4,
                useGrouping: true,
            },
            EUR: {
                style: 'currency',
                currency: 'EUR',
                currencyDisplay: 'narrowSymbol',
                notation: 'standard',
                maximumFractionDigits: 2,
                useGrouping: true,
            },
            USD: {
                style: 'currency',
                currency: 'USD',
                currencyDisplay: 'narrowSymbol',
                notation: 'standard',
                maximumFractionDigits: 2,
                useGrouping: true,
            },
            decimal: {
                style: 'decimal',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
                useGrouping: true,
            },
            integer: {
                style: 'decimal',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
                useGrouping: true,
            },
            compact: {
                style: 'decimal',
                notation: 'compact',
                compactDisplay: 'short',
                minimumFractionDigits: 0,
                maximumFractionDigits: 1,
                useGrouping: true,
            },
            seconds: {
                style: 'unit',
                unit: 'second',
                unitDisplay: 'long',
                maximumFractionDigits: 1,
            },
        },
        en: {
            currencyEUR: {
                style: 'currency',
                currency: 'EUR',
                currencySign: 'accounting',
                notation: 'standard',
                maximumFractionDigits: 4,
                useGrouping: true,
            },
            currencyUSD: {
                style: 'currency',
                currency: 'USD',
                currencySign: 'accounting',
                notation: 'standard',
                maximumFractionDigits: 4,
                useGrouping: true,
            },
            EUR: {
                style: 'currency',
                currency: 'EUR',
                currencyDisplay: 'narrowSymbol',
                notation: 'standard',
                maximumFractionDigits: 2,
                useGrouping: true,
            },
            USD: {
                style: 'currency',
                currency: 'USD',
                currencyDisplay: 'narrowSymbol',
                notation: 'standard',
                maximumFractionDigits: 2,
                useGrouping: true,
            },
            decimal: {
                style: 'decimal',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
                useGrouping: true,
            },
            integer: {
                style: 'decimal',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
                useGrouping: true,
            },
            compact: {
                style: 'decimal',
                notation: 'compact',
                compactDisplay: 'short',
                minimumFractionDigits: 0,
                maximumFractionDigits: 1,
                useGrouping: true,
            },
            seconds: {
                style: 'unit',
                unit: 'second',
                unitDisplay: 'long',
                maximumFractionDigits: 1,
            },
        },
    },
    datetimeFormats: {
        it: {
            short: {
                dateStyle: 'short',
            },
            'month-short': {
                month: 'short',
            },
            'date-medium': {
                dateStyle: 'medium',
            },
            'date-time': {
                dateStyle: 'short',
                timeStyle: 'short',
            },
            'date-time-medium': {
                dateStyle: 'medium',
                timeStyle: 'short',
            },
            time: {
                timeStyle: 'short',
            },
            'weekday-day': {
                weekday: 'short',
                day: 'numeric',
            },
        },
        en: {
            short: {
                dateStyle: 'short',
            },
            'month-short': {
                month: 'short',
            },
            'date-medium': {
                dateStyle: 'medium',
            },
            'date-time': {
                dateStyle: 'short',
                timeStyle: 'short',
            },
            'date-time-medium': {
                dateStyle: 'medium',
                timeStyle: 'short',
            },
            time: {
                timeStyle: 'short',
            },
            'weekday-day': {
                weekday: 'short',
                day: 'numeric',
            },
        },
    },
})

/**
 * `onResponse` hook for the better-auth clients. better-auth writes its error
 * messages in English only, so the `message` of an error response is replaced
 * by the translation of its code (`errors.<CODE>`), and every `error.message`
 * a page shows arrives in the user's language. A code with no translation
 * keeps better-auth's text, the `YOU_ARE_NOT_ALLOWED_TO_*` codes share one
 * message, a 429 reads as `errors.TOO_MANY_REQUESTS` whatever its body, and a
 * body that is not JSON (a proxy's page) gets the generic error instead of no
 * message. It never throws: a failure leaves the response as it came.
 */
export async function translateAuthError({
    response,
}: {
    response: Response
}): Promise<Response | undefined> {
    if (response.ok) {
        return
    }
    try {
        let body: Record<string, unknown> | undefined
        try {
            const parsed = await response.clone().json()
            body = parsed && typeof parsed === 'object' ? parsed : {}
        } catch {
            body = undefined
        }
        const code = typeof body?.code === 'string' ? body.code : undefined
        let key = `errors.${code}`
        if (response.status === 429) {
            key = 'errors.TOO_MANY_REQUESTS'
        } else if (code?.startsWith('YOU_ARE_NOT_ALLOWED_TO_')) {
            key = 'errors.NOT_ALLOWED'
        } else if (!body) {
            key = 'auth.genericError'
        }
        if (!i18n.global.te(key)) {
            return
        }
        return new Response(
            JSON.stringify({ ...body, message: i18n.global.t(key) }),
            {
                status: response.status,
                statusText: response.statusText,
                headers: response.headers,
            },
        )
    } catch {
        return
    }
}
